import { useEffect, useState } from 'react'

import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { EntryCollection } from 'contentful'
import { useSelector } from 'react-redux'

import {
  IRetailBannerFields,
  IRetailBannerItem,
} from '__generated__/contentful'

import { CONTENT_TYPES, contentfulClient } from 'utils/sdks/contentful'

import { isLiteRetailer, isProRetailer } from 'store/currentUser/selectors'

import {
  LINESHEET_CAROUSEL_LITE_RETAILERS_FLAG,
  LINESHEET_CAROUSEL_PRO_RETAILERS_FLAG,
} from './retailLinesheetsBanner.constants'
import contentfulUtils from 'features/Passport/contentful/utils'

export type RetailBannerItems = {
  title: string
  description?: string
  linkUrl: string
  imageUrl: string
  showTitle: boolean
}

type RetailDashboardContentfulData = {
  data: RetailBannerItems[]
  isLoading: boolean
  isError: boolean
}

const useRetailLinesheetBannerContentful = (
  contentType: string,
  isLinesheetPage: boolean,
): RetailDashboardContentfulData => {
  const { linesheetCarouselLiteRetailers, linesheetCarouselProRetailers } =
    useFlags()
  const [data, setData] = useState<RetailBannerItems[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const { isLiteRetailerAccount, isProRetailerAccount } = useSelector(
    (state) => ({
      isLiteRetailerAccount: isLiteRetailer(state),
      isProRetailerAccount: isProRetailer(state),
    }),
  )

  const query = {
    content_type: contentType,
  }

  const normalizeBannerItems = async (
    entries: EntryCollection<IRetailBannerFields>,
  ): Promise<RetailBannerItems[]> => {
    const parsedEntries =
      await contentfulClient.parseEntries<IRetailBannerFields>(entries)
    let itemIndex: number = -1

    const shouldViewLiteBanner =
      isLiteRetailerAccount &&
      linesheetCarouselLiteRetailers &&
      entries.items.find(
        (item) =>
          item.fields.launchDarklyId === LINESHEET_CAROUSEL_LITE_RETAILERS_FLAG,
      )
    const shouldViewProBanner =
      isProRetailerAccount &&
      linesheetCarouselProRetailers &&
      entries.items.find(
        (item) =>
          item.fields.launchDarklyId === LINESHEET_CAROUSEL_PRO_RETAILERS_FLAG,
      )

    if (shouldViewLiteBanner) {
      itemIndex = entries.items.findIndex(
        (item) =>
          item.fields.launchDarklyId === LINESHEET_CAROUSEL_LITE_RETAILERS_FLAG,
      )
    } else if (shouldViewProBanner) {
      itemIndex = entries.items.findIndex(
        (item) =>
          item.fields.launchDarklyId === LINESHEET_CAROUSEL_PRO_RETAILERS_FLAG,
      )
    }

    if (itemIndex === -1) {
      return []
    }

    // Filter out items that do not have a display image
    const bannerItems = parsedEntries.items[
      itemIndex
    ].fields.carouselItem.filter(
      (item) => item.fields.displayImage?.fields?.file?.url,
    )

    return bannerItems.map(({ fields }: IRetailBannerItem) => {
      return {
        title: fields.title,
        description: fields.description,
        linkUrl: fields.linkUrl ?? '',
        imageUrl: contentfulUtils.replaceProxyUrl(
          fields.displayImage?.fields?.file?.url ?? '',
        ),
        showTitle: fields.showTitle,
      }
    })
  }

  useEffect(() => {
    const getContentfulData = async (): Promise<void> => {
      try {
        const entries =
          await contentfulClient.getEntries<IRetailBannerFields>(query)
        const parsedEntries = await normalizeBannerItems(entries)
        setData(parsedEntries)
        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
        setIsError(true)
      }
    }

    if (isLinesheetPage) {
      getContentfulData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinesheetPage, isLoading])

  return { data, isLoading, isError }
}

export { CONTENT_TYPES, useRetailLinesheetBannerContentful }
